
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import {
  Plus,
  Edit,
  Slack,
  Mail,
  Calendar,
  Video,
  Phone,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronRight,
} from "lucide-react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { Switch } from "./ui/switch"
import { getOrgEqTrackersDashboard, updateOrgEqTracker } from "../../api/helpers"

interface Tracker {
  tracker_id: string
  tracker_name: string
  description: string
  team: string | null
  start_date: string | null
  end_date: string | null
  total_hits: number
  trend: "up" | "down" | "stable"
  trend_percentage: number
  top_emotions: Array<{
    emotion: string
    percentage: number
    total_occurrences?: number
  }>
  positivity_percentage: number
  summary?: string
  summary_generated_at?: string
  last_triggered: string | null
  last_triggered_timestamp: string | null
  active: boolean
  sources?: string[]
  data_sources?: string[]
  visibility?: string
}

const sourceIcons = {
  slack: Slack,
  gmail: Mail,
  gcal: Calendar,
  zoom: Video,
  gong: Phone,
  voice: Phone,
  tickets: FileText,
  notes: MessageSquare,
  calls: Video,
  emails: Mail,
  chats: MessageSquare,
}

interface EmotionTrackersListProps {
  onCreateTracker: () => void
  onViewTracker: (trackerId: string, tracker?: Tracker) => void
  onEditTracker: (trackerId: string, tracker?: Tracker) => void
}

export function EmotionTrackersList({ onCreateTracker, onViewTracker, onEditTracker }: EmotionTrackersListProps) {
  const [trackers, setTrackers] = useState<Tracker[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const user = useSelector((state: any) => state.auth.user)

  const loadTrackers = async () => {
    if (!user?.organization_info?._id) {
      setError("Organization not found. Please ensure you're logged in.")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await getOrgEqTrackersDashboard(user.organization_info._id, "day", false)
      console.log("Trackers dashboard API response:", response)
      console.log("🔍 Tracker field debugging:", response?.trackers?.map((t: any) => ({
        name: t.tracker_name,
        active: t.active,
        visibility: t.visibility,
        sources: t.sources,
        data_sources: t.data_sources,
        last_triggered: t.last_triggered,
        allKeys: Object.keys(t)
      })))
      setTrackers(response?.trackers || [])
      console.log("✅ Trackers loaded. Active status check:", response?.trackers?.map((t: any) => ({
        name: t.tracker_name,
        active: t.active,
        type: typeof t.active
      })))
    } catch (err: any) {
      console.error("Trackers dashboard API error:", err)
      setError(err.response?.data?.message || "Failed to load trackers")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log("EmotionTrackersList useEffect triggered with:", {
      orgId: user?.organization_info?._id
    })
    loadTrackers()
  }, [user?.organization_info?._id])

  const toggleTrackerStatus = async (trackerId: string) => {
    if (!user?.organization_info?._id) return

    const tracker = trackers.find(t => t.tracker_id === trackerId)
    if (!tracker) return

    try {
      // Optimistically update the UI
      setTrackers((prev) =>
        prev.map((t) =>
          t.tracker_id === trackerId ? { ...t, active: !t.active } : t,
        ),
      )

      await updateOrgEqTracker(user.organization_info._id, trackerId, {
        active: !tracker.active
      })

      // Success - the optimistic update was correct
    } catch (err: any) {
      // Revert the optimistic update on error
      setTrackers((prev) =>
        prev.map((t) =>
          t.tracker_id === trackerId ? { ...t, active: tracker.active } : t,
        ),
      )
      setError(err.response?.data?.message || "Failed to update tracker status")
    }
  }

  const toggleRowExpansion = (trackerId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(trackerId)) {
        newSet.delete(trackerId)
      } else {
        newSet.add(trackerId)
      }
      return newSet
    })
  }

  const mapVisibilityForDisplay = (apiVisibility: string) => {
    // Map API values to display values
    if (apiVisibility === "public") return "organization"
    return apiVisibility
  }

  const getVisibilityLabel = (visibility: string) => {
    const mapped = mapVisibilityForDisplay(visibility)
    switch (mapped) {
      case "private":
        return "Private"
      case "team":
        return "Team"
      case "organization":
        return "Organization"
      default:
        return visibility || "Not set"
    }
  }

  const getVisibilityBadgeColor = (visibility: string) => {
    const mapped = mapVisibilityForDisplay(visibility)
    switch (mapped) {
      case "private":
        return "bg-gray-200 text-gray-900"
      case "team":
        return "bg-blue-100 text-blue-800"
      case "organization":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button onClick={onCreateTracker} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Tracker
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadTrackers} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={onCreateTracker} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Tracker
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Loading trackers...</p>
          <p className="text-xs text-muted-foreground mt-2">
            Org ID: {user?.organization_info?._id || "Not found"}
          </p>
        </div>
      ) : trackers.length === 0 ? (
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No trackers found</h3>
              <p className="text-muted-foreground mb-4">
                Get started by creating your first tracker to monitor emotions across your communications.
              </p>
              <Button onClick={onCreateTracker}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Tracker
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Active Trackers</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Sources</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Hit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trackers.map((tracker) => {
                  // Debug: Log each tracker's active status during render
                  if (tracker.tracker_name) {
                    console.log(`🔍 Rendering tracker "${tracker.tracker_name}" - active:`, tracker.active, typeof tracker.active)
                  }
                  return (
                  <>
                    <TableRow
                      key={tracker.tracker_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRowExpansion(tracker.tracker_id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {expandedRows.has(tracker.tracker_id) ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="font-medium text-primary">{tracker.tracker_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getVisibilityBadgeColor(tracker.visibility || "unknown")}>
                          {getVisibilityLabel(tracker.visibility || "")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(() => {
                            const sources = tracker.data_sources || tracker.sources || []
                            return sources.length > 0 ? (
                              sources.map((source) => {
                                const Icon = sourceIcons[source as keyof typeof sourceIcons]
                                return Icon ? <Icon key={source} className="h-4 w-4 text-muted-foreground" /> : null
                              })
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={tracker.active}
                            onCheckedChange={(checked) => {
                              toggleTrackerStatus(tracker.tracker_id)
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                          />
                          <span className="text-sm">{tracker.active ? "Active" : "Paused"}</span>
                        </div>
                      </TableCell>
                      <TableCell>{tracker.last_triggered || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEditTracker(tracker.tracker_id, tracker)
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(tracker.tracker_id) && (
                      <TableRow key={`${tracker.tracker_id}-expanded`}>
                        <TableCell colSpan={6} className="bg-muted/30 border-t-0">
                          <div className="py-3 px-4">
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-muted-foreground">Description</div>
                              <div className="text-sm break-words whitespace-pre-wrap overflow-wrap-anywhere max-w-full pr-8">
                                {tracker.description || "No description provided"}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )})}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
